<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { useValidators } from "../..//helpers/utils"

const props = defineProps<{
  modelValue: any
  optionsFunction?: (searchText: any) => Promise<any[]>
  options?: Array<any>
  displayText?: string
  label?: string
  idField?: string
  textField?: string
  debounce?: number
  placeholder?: string
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  multiple?: boolean
  [key: string]: any
  required?: boolean
}>()

const emit = defineEmits(["update:modelValue"])
const selectKey = ref(0)
const searchValue = ref("")
const options = ref<any[]>([])
const debounceTimeout = ref<any>(null)
const validators = useValidators()

const inputValue = computed({
  get: () => props.modelValue ?? (props.multiple ? [] : null),
  set: (value) => emit("update:modelValue", value),
})

watch(
  () => props.options,
  async (val) => {
    if (val && Array.isArray(val)) {
      const newOptions = val.map((item: any) => ({
        value: item[props.idField ?? "id"],
        text: item[props.textField ?? "name"] ?? item[props.displayName ?? "name"],
      }))

      // Only update if options actually changed
      if (JSON.stringify(newOptions) !== JSON.stringify(options.value)) {
        options.value = newOptions
        // next tick to force re-render
        await nextTick()
        selectKey.value += 1
      }
    } else {
      options.value = []
    }
  },
  { immediate: true },
)

watch(searchValue, (val) => {
  if (props.optionsFunction && val && val.length > 0) {
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }

    debounceTimeout.value = setTimeout(async () => {
      try {
        if (props.optionsFunction) {
          const response = await props.optionsFunction(val)
          options.value = response.map((item: any) => ({
            value: item[props.idField ?? "id"],
            text: item[props.textField ?? "name"] ?? item[props.displayName ?? "name"],
          }))
        }
      } catch (error) {
        console.error("Error fetching options:", error)
      }
    }, props.debounce ?? 300)
  }
})

watch(
  () => props.displayText,
  (newVal) => {
    if (newVal && inputValue.value) {
      const displayOption = { value: inputValue.value, text: newVal }

      // Only update if the display option is different
      const hasDisplayOption = options.value.some(
        (opt) => opt.value === displayOption.value && opt.text === displayOption.text,
      )

      if (!hasDisplayOption) {
        options.value = [displayOption]
      }
    }
  },
  { immediate: true },
)

const searchFunction = () => {
  return true
}

const deleteChip = (chip: any) => {
  if (Array.isArray(inputValue.value)) {
    inputValue.value = inputValue.value.filter((v) => v !== chip)
  }
}

const getChipDisplayValue = (option: any) => {
  return option ? option[props.textField ?? "name"] : ""
}
</script>

<template>
  <VaSelect
    :key="selectKey"
    v-bind="props"
    v-model="inputValue"
    v-model:search="searchValue"
    :searchable="props.searchable || !!props.optionsFunction"
    value-by="value"
    text-by="text"
    :clearable="props.clearable"
    :disabled="props.disabled"
    :label="props.label"
    :options="options"
    :class="{ 'va-multiple': props.multiple }"
    :highlight-matched-text="false"
    :search-fn="props.optionsFunction ? searchFunction : undefined"
    :placeholder="props.placeholder"
    :rules="props.required ? [validators.required] : undefined"
  >
    <template #content="{ value }" v-if="props.multiple">
      <VaChip
        v-for="chip in value"
        :key="chip"
        size="small"
        class="mr-1 my-1"
        closeable
        @update:model-value="deleteChip(chip[props.idField ?? 'id'])"
      >
        {{ getChipDisplayValue(chip) }}
      </VaChip>
    </template>
  </VaSelect>
</template>

<style>

.va-multiple .va-input-wrapper__field {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

</style>
